import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const scopes = [
  {
    icon: "ri-film-line",
    title: "亚洲电影",
    desc: "收录中国、日本、韩国、泰国、印度等国家和地区的剧情、悬疑、爱情与文艺佳作。",
  },
  {
    icon: "ri-tv-2-line",
    title: "电视剧集",
    desc: "涵盖古装、年代、都市、治愈等多种题材，按地区与年份清晰分类，方便追剧。",
  },
  {
    icon: "ri-mic-2-line",
    title: "综艺节目",
    desc: "美食竞技、音乐选秀、旅行纪实与情感观察，满足轻松娱乐的观看需求。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "主题专题",
    desc: "由编辑精选的影视合集，用主题串联作品，帮你高效发现值得一看的内容。",
  },
];

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们 - 亚洲一区二区在线",
    description:
      "了解亚洲一区二区在线：我们专注整合亚洲电影、电视剧与综艺内容，提供多维筛选、评分参考与专题推荐，让找片更简单。",
    keywords: "关于亚洲一区二区在线,亚洲影视平台,亚洲电影,亚洲电视剧,亚洲综艺",
  });

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <section className="mt-5 overflow-hidden rounded-lg">
        <div className="relative h-56 w-full md:h-72">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20banner%2C%20modern%20minimal%20cinema%20hall%20with%20rows%20of%20seats%20and%20soft%20ambient%20light%2C%20elegant%20professional%20atmosphere%2C%20deep%20blue%20and%20warm%20amber%20tones%2C%20high%20detail%2C%20artistic%2C%20empty%20space%20for%20text&width=1600&height=700&seq=about-banner&orientation=landscape"
            alt="亚洲一区二区在线 品牌介绍"
            title="关于亚洲一区二区在线"
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-4 md:px-8">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-movie-2-fill"></i>
              关于我们
            </span>
            <h1 className="mt-3 max-w-xl font-heading text-2xl font-bold text-background-50 md:text-4xl">
              亚洲一区二区在线
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-background-300 md:text-base">
              专注整合亚洲影视内容，让每一次找片都更高效、更愉快。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            我们的使命
          </h2>
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700">
            <p>
              亚洲一区二区在线是一个面向亚洲影视爱好者的内容浏览与资讯平台。我们希望用清晰的结构和
              <strong className="text-foreground-900">多维度的筛选方式</strong>
              ，帮助观众在丰富的影视作品中快速找到符合口味的那一部。
            </p>
            <p>
              无论是最新上线的电影、热播的剧集，还是轻松有趣的综艺节目，你都可以按类型、地区、
              年份和热度自由组合筛选，并通过评分与简介快速判断是否值得一看。
            </p>
            <p>
              我们还定期推出
              <strong className="text-foreground-900">主题专题合集</strong>
              ，用一条线索把风格相近的作品串联起来，让浏览本身也成为一种乐趣。
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
            <i className="ri-mail-send-line text-primary-500"></i>
            联系我们
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-foreground-700">
            <li className="flex items-start gap-2">
              <i className="ri-mail-line mt-0.5 text-foreground-400"></i>
              <span>
                邮箱：
                <a
                  href="mailto:service@yzyq2q-demo.com"
                  className="text-primary-600 hover:text-primary-700"
                >
                  service@yzyq2q-demo.com
                </a>
              </span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-customer-service-2-line mt-0.5 text-foreground-400"></i>
              <span>合作与反馈：欢迎通过邮件与我们取得联系</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-time-line mt-0.5 text-foreground-400"></i>
              <span>服务时间：每日 09:00 - 23:00</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          内容范围
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {scopes.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-lg border border-background-200 bg-secondary-50 p-5 md:p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
          <i className="ri-copyright-line text-primary-500"></i>
          版权声明
        </h2>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-700">
          <p>
            本站为演示性质的影视内容浏览站点，站内所有影视名称、评分、导演、演员及剧情信息均为
            <strong className="text-foreground-900">虚构示例</strong>
            ，仅用于展示页面功能与交互效果，不涉及任何真实版权内容。
          </p>
          <p>
            本站不提供真实影视播放服务，页面中的「立即观看」「预告视频」等均为前端演示，不包含可播放的视频资源。
            如对内容有任何疑问，欢迎通过上方邮箱与我们联系。
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-5-line"></i>
            返回首页
          </Link>
          <Link
            to="/category/movie"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-compass-3-line"></i>
            浏览影视分类
          </Link>
        </div>
      </section>
    </div>
  );
}